import React, { useEffect, useState, useRef } from "react";
import "./datatable.scss";
import axios from "../../../../../api/axios";
import Modal from '@mui/material/Modal';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

const VendorsDatatable = () => {
  const [vendorsData, setVendorsData] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [newVendorName, setNewVendorName] = useState('');
  const [errMsg, setErrMsg] = useState('');
  const [currentVendorId, setCurrentVendorId] = useState(null);
  const [editingVendor, setEditingVendor] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const errRef = useRef();

  const fetchData = async () => {
    const restaurantId = sessionStorage.getItem("configBizEntityId");
    try {
      const response = await axios.get(`home/getAllVendors/${restaurantId}`, {
        headers: { "Content-Type": "application/json" },
        withCredentials: false,
      });
      setVendorsData(response.data.data || []);
    } catch (error) {
      console.error('Error fetching vendor data:', error);
      setVendorsData([]);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAddVendor = () => {
    setOpenModal(true);
    setNewVendorName('');
    setEditingVendor(null);
    setErrMsg('');
  };

  const handleEditVendor = (vendor) => {
    setEditingVendor(vendor);
    setNewVendorName(vendor.vendor_name);
    setOpenModal(true);
    setErrMsg('');
  };

  const handleSaveVendor = async () => {
    if (!newVendorName.trim()) {
      setErrMsg('Vendor name is required');
      return;
    }

    const restaurantId = sessionStorage.getItem("configBizEntityId");
    
    try {
      let response;
      if (editingVendor) {
        // Update existing vendor
        response = await axios.put(
          `home/editVendor/${editingVendor.id}`,
          {
            id: editingVendor.id,
            vendor_name: newVendorName,
            business_entity_id: restaurantId
          },
          {
            headers: { "Content-Type": "application/json" },
            withCredentials: false,
          }
        );
      } else {
        // Add new vendor
        response = await axios.post(
          `home/addVendor`,
          {
            vendor_name: newVendorName,
            business_entity_id: restaurantId
          },
          {
            headers: { "Content-Type": "application/json" },
            withCredentials: false,
          }
        );
      }

      if (response.status === 201 || response.status === 200 || response.data.message) {
        setSnackbarMessage(editingVendor ? 'Vendor updated successfully' : 'Vendor added successfully');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setOpenModal(false);
        setEditingVendor(null);
        fetchData();
      } else {
        setErrMsg(response.data.message || `Failed to ${editingVendor ? 'update' : 'add'} vendor`);
      }
    } catch (error) {
      console.error(`Error ${editingVendor ? 'updating' : 'adding'} vendor:`, error);
      setErrMsg(`Failed to ${editingVendor ? 'update' : 'add'} vendor`);
    }
  };

  const handleDeleteVendor = (vendorId) => {
    setCurrentVendorId(vendorId);
    setOpenDeleteDialog(true);
  };

  const confirmDeleteVendor = async () => {
    try {
      const response = await axios.delete(
        `home/deleteVendor/${currentVendorId}`,
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: false,
        }
      );

      if (response.data.message) {
        setSnackbarMessage('Vendor deleted successfully');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        fetchData();
      } else {
        setErrMsg(response.data.message || 'Failed to delete vendor');
      }
    } catch (error) {
      console.error('Error deleting vendor:', error);
      setErrMsg('Failed to delete vendor');
    } finally {
      setOpenDeleteDialog(false);
      setCurrentVendorId(null);
    }
  };

  
  return (
    <div className="datatable" style={{ padding: '20px' }}>
      <div className="datatableTitle" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>Vendors Management</h2>
        <button className="link" onClick={handleAddVendor} style={{ padding: '8px 16px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
          Add New Vendor
        </button>
      </div>
      
      {/* Card Grid Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px', marginBottom: '20px' }}>
        {vendorsData.map((vendor) => (
          <div key={vendor.id} style={{ 
            border: '1px solid #ddd', 
            borderRadius: '8px', 
            padding: '20px', 
            backgroundColor: 'white',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            transition: 'transform 0.2s'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <div>
                <h3 style={{ margin: 0, color: '#333', fontSize: '18px' }}>{vendor.vendor_name}</h3>
              </div>
              <div>
                <button 
                  onClick={() => handleEditVendor(vendor)} 
                  style={{ 
                    padding: '6px 12px', 
                    backgroundColor: '#2196F3', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    marginRight: '8px'
                  }}
                >
                  Edit
                </button>
                <button 
                  onClick={() => handleDeleteVendor(vendor.id)} 
                  style={{ 
                    padding: '6px 12px', 
                    backgroundColor: '#f44336', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '4px', 
                    cursor: 'pointer'
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Fallback to DataGrid for large datasets */}
      {vendorsData.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          No vendors found. Click "Add New Vendor" to get started.
        </div>
      )}

      {/* Add/Edit Vendor Modal */}
      <Modal
        open={openModal}
        onClose={() => {
          setOpenModal(false);
          setEditingVendor(null);
        }}
        aria-labelledby="vendor-modal"
        aria-describedby="vendor-modal-description"
      >
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'white',
          padding: '30px',
          borderRadius: '12px',
          minWidth: '450px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
        }}>
          <h2 style={{ marginBottom: '20px', color: '#333' }}>
            {editingVendor ? 'Edit Vendor' : 'Add New Vendor'}
          </h2>
          <p ref={errRef} className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive" style={{ color: 'red', marginBottom: '10px' }}>
            {errMsg}
          </p>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#555' }}>Vendor Name *</label>
            <input
              type="text"
              value={newVendorName}
              onChange={(e) => setNewVendorName(e.target.value)}
              style={{ 
                width: '100%', 
                padding: '12px', 
                marginTop: '5px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px'
              }}
              placeholder="Enter vendor name"
              autoFocus
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '25px' }}>
            <Button 
              onClick={() => {
                setOpenModal(false);
                setEditingVendor(null);
              }} 
              style={{ 
                padding: '10px 20px',
                border: '1px solid #ddd',
                backgroundColor: '#f5f5f5',
                color: '#666',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveVendor} 
              variant="contained"
              style={{ 
                padding: '10px 24px',
                backgroundColor: editingVendor ? '#2196F3' : '#4CAF50',
                color: 'white',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              {editingVendor ? 'Update Vendor' : 'Add Vendor'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm Delete"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this vendor? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={confirmDeleteVendor} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity} 
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default VendorsDatatable;
